import React, { useState, useEffect, useCallback } from 'react';
import Network from '../../../models/Network.js';
import URLS from '../../../urls.js';
import './Module.css';

const AnalysisResultsModule = ({ user, reportId, isSmallScreen }) => {
    const [analysisData, setAnalysisData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [formData, setFormData] = useState({
        issue: '',
        protocol: '',
        evidence: '',
        severity: 'minor',
        type: ''
    });
    const [customType, setCustomType] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const loadAnalysisResults = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            // Fetch findings from database via API
            const url = URLS.GET_ONE_REPORT_FINDINGS;
            const dict = {
                uid: user.uid,
                report_id: reportId,
                CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
            };

            await Network.post(url, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    setAnalysisData(response.data);
                } else {
                    console.error('Error fetching analysis results:', response.status);
                    setError('Unable to load analysis results. Please ensure Agent2 analysis has been completed.');
                }
            });
        } catch (err) {
            console.error('Error loading analysis results:', err);
            setError('Unable to load analysis results. Please ensure Agent2 analysis has been completed.');
        } finally {
            setLoading(false);
        }
    }, [user, reportId]);

    useEffect(() => {
        if (reportId && user) {
            loadAnalysisResults();
        }
    }, [reportId, user, loadAnalysisResults]);

    const handleAddFinding = async () => {
        const finalType = formData.type === 'Other' ? customType.trim() : formData.type;
        
        if (!formData.issue.trim() || !formData.protocol.trim() || !formData.evidence.trim() || !finalType) {
            alert('Please fill in all required fields: Issue, Protocol, Evidence, and Type');
            return;
        }

        if (formData.type === 'Other' && !customType.trim()) {
            alert('Please specify the finding type in the custom type field');
            return;
        }

        setSubmitting(true);
        const url = URLS.ADD_USER_FINDING;
        const dict = {
            uid: user.uid,
            report_id: reportId,
            issue: formData.issue.trim(),
            protocol: formData.protocol.trim(),
            evidence: formData.evidence.trim(),
            severity: formData.severity,
            type: finalType,
            user_name: user.ownerName || 'User',
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                // Reload findings
                loadAnalysisResults();
                // Reset form
                setFormData({ issue: '', protocol: '', evidence: '', severity: 'minor', type: '' });
                setCustomType('');
                setShowAddForm(false);
            } else {
                alert('Failed to add finding. Please try again.');
            }
            setSubmitting(false);
        });
    };

    const handleDeleteFinding = async (findingsId) => {
        if (!window.confirm('Are you sure you want to delete this finding? This action cannot be undone.')) {
            return;
        }

        setSubmitting(true);
        const url = URLS.DELETE_USER_FINDING;
        const dict = {
            findings_id: findingsId,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                // Reload findings
                loadAnalysisResults();
            } else {
                alert('Failed to delete finding. Please try again.');
            }
            setSubmitting(false);
        });
    };

    const handleValidateFinding = async (findingsId, currentValidationStatus) => {
        const newStatus = !currentValidationStatus;
        setSubmitting(true);
        const url = URLS.VALIDATE_FINDING;
        const dict = {
            findings_id: findingsId,
            validation_status: newStatus,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                // Reload findings
                loadAnalysisResults();
            } else {
                alert('Failed to update validation status. Please try again.');
            }
            setSubmitting(false);
        });
    };

    const handleFlagFinding = async (findingsId, currentFlagStatus) => {
        const newStatus = !currentFlagStatus;
        setSubmitting(true);
        const url = URLS.FLAG_FINDING;
        const dict = {
            findings_id: findingsId,
            flag_status: newStatus,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                // Reload findings
                loadAnalysisResults();
            } else {
                alert('Failed to update flag status. Please try again.');
            }
            setSubmitting(false);
        });
    };

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical':
                return '#dc3545';
            case 'moderate':
                return '#ffc107';
            case 'minor':
                return '#17a2b8';
            default:
                return '#6c757d';
        }
    };

    const getComplianceColor = (compliance) => {
        switch (compliance?.toLowerCase()) {
            case 'compliant':
                return '#28a745';
            case 'non-compliant':
                return '#dc3545';
            case 'partially compliant':
                return '#ffc107';
            default:
                return '#6c757d';
        }
    };

    if (loading) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div>Loading analysis results...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px', color: '#dc3545' }}>
                    <div>{error}</div>
                </div>
            </div>
        );
    }

    if (!analysisData) {
        return (
            <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
                <h2 className='module-title'>Analysis Results</h2>
                <div style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                    <div>No analysis results available</div>
                </div>
            </div>
        );
    }

    const { analysisResults, timestamp, reportId: storedReportId } = analysisData;

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <h2 className='module-title'>Analysis Results</h2>
            
            {/* Analysis Overview */}
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <h3 style={{ margin: 0, color: '#495057' }}>Analysis Overview</h3>
                    <span style={{ fontSize: '0.9em', color: '#6c757d' }}>
                        {new Date(timestamp).toLocaleString()}
                    </span>
                </div>
                
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ 
                            fontSize: '24px', 
                            fontWeight: 'bold',
                            color: getComplianceColor(analysisResults.overall_compliance)
                        }}>
                            {analysisResults.overall_compliance?.toUpperCase() || 'UNKNOWN'}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Overall Compliance</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#495057' }}>
                            {analysisResults.statistics?.total_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Total Findings</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545' }}>
                            {analysisResults.statistics?.critical_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Critical</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107' }}>
                            {analysisResults.statistics?.moderate_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Moderate</div>
                    </div>
                    
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8' }}>
                            {analysisResults.statistics?.minor_findings || 0}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#6c757d' }}>Minor</div>
                    </div>
                </div>
            </div>

            {/* Summary */}
            {analysisResults.summary && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e9ecef', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>Summary</h4>
                    <p style={{ margin: 0, lineHeight: '1.5', color: '#495057' }}>
                        {analysisResults.summary}
                    </p>
                </div>
            )}

            {/* Add Finding Button and Form */}
            <div style={{ marginBottom: '20px' }}>
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                >
                    {showAddForm ? 'Cancel' : '+ Add Finding'}
                </button>

                {showAddForm && (
                    <div style={{
                        marginTop: '15px',
                        padding: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #dee2e6'
                    }}>
                        <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>Add New Finding</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                                    Issue <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <textarea
                                    value={formData.issue}
                                    onChange={(e) => setFormData({ ...formData, issue: e.target.value })}
                                    style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        padding: '8px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    placeholder="Describe the issue..."
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                                    Protocol <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    value={formData.protocol}
                                    onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    placeholder="Protocol name or source..."
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                                    Evidence <span style={{ color: '#dc3545' }}>*</span>
                                </label>
                                <textarea
                                    value={formData.evidence}
                                    onChange={(e) => setFormData({ ...formData, evidence: e.target.value })}
                                    style={{
                                        width: '100%',
                                        minHeight: '80px',
                                        padding: '8px',
                                        border: '1px solid #ced4da',
                                        borderRadius: '4px',
                                        fontSize: '14px'
                                    }}
                                    placeholder="Provide evidence or justification..."
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                                        Severity <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <select
                                        value={formData.severity}
                                        onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ced4da',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="minor">Minor</option>
                                        <option value="moderate">Moderate</option>
                                        <option value="critical">Critical</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>
                                        Type <span style={{ color: '#dc3545' }}>*</span>
                                    </label>
                                    <select
                                        value={formData.type}
                                        onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            border: '1px solid #ced4da',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <option value="">Select a type...</option>
                                        <option value="Incorrect Clinical Action">Incorrect Clinical Action</option>
                                        <option value="Incorrect Provider Level for Clinical Action">Incorrect Provider Level for Clinical Action</option>
                                        <option value="Missing Clinical Action">Missing Clinical Action</option>
                                        <option value="Incorrect Medication Administration">Incorrect Medication Administration</option>
                                        <option value="Contraindicated Medication">Contraindicated Medication</option>
                                        <option value="Incorrect Procedure Performed">Incorrect Procedure Performed</option>
                                        <option value="Contraindicated Procedure">Contraindicated Procedure</option>
                                        <option value="Other">Other</option>
                                    </select>
                                    {formData.type === 'Other' && (
                                        <input
                                            type="text"
                                            value={customType}
                                            onChange={(e) => setCustomType(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ced4da',
                                                borderRadius: '4px',
                                                fontSize: '14px',
                                                marginTop: '8px'
                                            }}
                                            placeholder="Specify custom finding type..."
                                        />
                                    )}
                                </div>
                            </div>
                            <div>
                                <button
                                    onClick={handleAddFinding}
                                    disabled={submitting}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: submitting ? '#6c757d' : '#28a745',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {submitting ? 'Adding...' : 'Add Finding'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Findings */}
            {analysisResults.findings && analysisResults.findings.length > 0 ? (
                <div>
                    <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>Detailed Findings</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {analysisResults.findings.map((finding, index) => {
                            const severityColor = getSeverityColor(finding.severity);
                            // Determine background color based on severity (similar to FLE's result-based coloring)
                            const backgroundColor = severityColor === '#dc3545' ? '#fad4d4' : 
                                                   severityColor === '#ffc107' ? '#fff3cd' : 
                                                   severityColor === '#17a2b8' ? '#d1ecf1' : '#eee';
                            
                            return (
                                <div 
                                    key={finding.id || index}
                                    className="list-element"
                                    style={{ backgroundColor }}
                                >
                                    <div className="small-div" style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div className='large-div' style={{ display: 'flex', flexDirection: 'row' }}>
                                            <div className="tiny-div">
                                            </div>
                                            <div className="large-div" style={{ justifyContent: 'center' }}>
                                                <div className="element-name" style={{ 
                                                    fontSize: 'larger',
                                                    color: severityColor,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {finding.severity} - {finding.type}
                                                </div>
                                            </div>
                                            <div className="tiny-div" style={{ justifyContent: 'right' }}>
                                            </div>
                                        </div>
                                        <div className='large-div' style={{ display: 'flex', flexDirection: 'row' }}>
                                            <div className="small-div" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <div style={{ fontSize: '16px' }}>{finding.protocol}</div>
                                            </div>
                                            <div className="large-div" style={{ justifyContent: 'center' }}>
                                                <div className="element-name" style={{ fontSize: '16px', marginTop: 'auto' }}>
                                                </div>
                                            </div>
                                            <div className="small-div" style={{ justifyContent: 'right', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {/* Validate button - only for non-manually-added findings (CADMUS-generated) */}
                                                {!finding.manually_added && finding.findings_id && (
                                                    <button
                                                        onClick={() => handleValidateFinding(finding.findings_id, finding.manual_validation)}
                                                        disabled={submitting}
                                                        style={{
                                                            padding: '5px 10px',
                                                            backgroundColor: submitting ? '#6c757d' : (finding.manual_validation ? '#28a745' : '#6c757d'),
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold'
                                                        }}
                                                        title={finding.manual_validation ? "Mark as not validated" : "Mark as validated"}
                                                    >
                                                        {finding.manual_validation ? '✓ Validated' : 'Validate'}
                                                    </button>
                                                )}
                                                {/* Flag button - for all findings */}
                                                {finding.findings_id && (
                                                    <button
                                                        onClick={() => handleFlagFinding(finding.findings_id, finding.flag)}
                                                        disabled={submitting}
                                                        style={{
                                                            padding: '5px 10px',
                                                            backgroundColor: submitting ? '#6c757d' : (finding.flag ? '#ffc107' : '#6c757d'),
                                                            color: finding.flag ? '#000' : 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold'
                                                        }}
                                                        title={finding.flag ? "Remove flag" : "Flag this finding"}
                                                    >
                                                        {finding.flag ? '🚩 Flagged' : 'Flag'}
                                                    </button>
                                                )}
                                                {/* Delete button - only for manually-added findings */}
                                                {finding.manually_added && finding.findings_id && (
                                                    <button
                                                        onClick={() => handleDeleteFinding(finding.findings_id)}
                                                        disabled={submitting}
                                                        style={{
                                                            padding: '5px 10px',
                                                            backgroundColor: submitting ? '#6c757d' : '#dc3545',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: submitting ? 'not-allowed' : 'pointer',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold'
                                                        }}
                                                        title="Delete this finding"
                                                    >
                                                        Delete
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="small-div" style={{ marginTop: 20, display: 'flex', flexDirection: 'column' }}>
                                            <p style={{ margin: 0, marginBottom: '10px' }}><strong><u>Issue</u>:</strong> {finding.issue}</p>
                                            <p style={{ margin: 0 }}><strong><u>Evidence</u>:</strong> {finding.evidence}</p>
                                            {finding.author && (
                                                <p style={{ margin: '10px 0 0 0', fontSize: '0.9em', color: '#6c757d', fontStyle: 'italic' }}>
                                                    Author: {finding.author} {finding.manually_added ? '(User Added)' : '(CADMUS)'}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '20px', 
                    backgroundColor: '#d4edda', 
                    borderRadius: '8px',
                    border: '1px solid #c3e6cb'
                }}>
                    <div style={{ fontSize: '18px', color: '#155724', marginBottom: '5px' }}>
                        ✅ No Findings Identified
                    </div>
                    <div style={{ color: '#155724' }}>
                        The report appears to be compliant with all applicable protocols.
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalysisResultsModule;
